import { Box, TextField } from '@mui/material';
import type { Language, Languages, LocalizedLabel } from '../../types';

interface LocalizedLabelEditorProps {
  value: LocalizedLabel | undefined;
  onChange: (value: LocalizedLabel | undefined) => void;
  languages: Languages;
  defaultLabel?: LocalizedLabel;
  label?: string;
}

function resolveLabel(
  label: LocalizedLabel | undefined,
  language: Language,
  fallback: string,
): string {
  if (!label) return fallback;
  return label[language] || fallback;
}

export function LocalizedLabelEditor({
  value,
  onChange,
  languages,
  defaultLabel,
  label,
}: LocalizedLabelEditorProps) {
  const primaryLanguage = languages[0] || 'en';

  // Single language mode
  if (languages.length === 1) {
    const lang = languages[0];
    const defaultValue = resolveLabel(defaultLabel, lang, '');
    // Show override if it exists and is not empty, otherwise show default
    const hasOverride = value && value[lang]?.trim();
    const displayValue = hasOverride ? value[lang] || '' : defaultValue;

    return (
      <TextField
        fullWidth
        size="small"
        label={label}
        value={displayValue}
        onChange={(e) => {
          const newValue = e.target.value;
          // If the value matches the default, clear the override to use default
          // Otherwise, set the override
          if (newValue === defaultValue || !newValue.trim()) {
            onChange(undefined);
          } else {
            const otherLang = lang === 'en' ? 'es' : 'en';
            const newLabel: LocalizedLabel = {
              [lang]: newValue,
              [otherLang]: value?.[otherLang] || defaultLabel?.[otherLang] || '',
            } as LocalizedLabel;
            onChange(newLabel);
          }
        }}
        onBlur={(e) => {
          const trimmed = e.target.value.trim();
          if (!trimmed || trimmed === defaultValue) {
            onChange(undefined);
          }
        }}
      />
    );
  }

  // Multi-language mode
  const currentValue = value || { en: '', es: '' };

  const handleLanguageChange = (lang: Language, newValue: string) => {
    onChange({
      ...currentValue,
      [lang]: newValue,
    });
  };

  return (
    <Box>
      {languages.map((lang) => {
        const defaultValue = resolveLabel(defaultLabel, lang, '');
        // Show override if it exists and is not empty, otherwise show default
        const langOverride = currentValue[lang]?.trim();
        const displayValue = langOverride || defaultValue;

        return (
          <TextField
            key={lang}
            fullWidth
            size="small"
            label={label ? `${label} (${lang.toUpperCase()})` : lang.toUpperCase()}
            value={displayValue}
            onChange={(e) => {
              const newValue = e.target.value;
              // If the value matches the default or is empty, use default
              if (newValue === defaultValue || !newValue.trim()) {
                if (defaultLabel) {
                  onChange(defaultLabel);
                } else {
                  onChange({
                    ...currentValue,
                    [lang]: '',
                  });
                }
              } else {
                handleLanguageChange(lang, newValue);
              }
            }}
            margin="dense"
            onBlur={(e) => {
              const trimmed = e.target.value.trim();
              if (!trimmed || trimmed === defaultValue) {
                if (defaultLabel) {
                  onChange(defaultLabel);
                } else {
                  onChange({
                    ...currentValue,
                    [lang]: '',
                  });
                }
              }
            }}
          />
        );
      })}
    </Box>
  );
}
